import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getListOptions as getCompanyListOptions } from '@dao/CompanyDAO';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  normalizeCompanyOptions,
  parseStoredCompanyId,
  resolveActiveCompanyId,
  shouldPersistActiveCompanyId,
  type CompanyOption,
} from '@/shared/utils/activeCompany';

export const ACTIVE_COMPANY_STORAGE_KEY = 'selectedCompanyId';

type ActiveCompanyContextValue = {
  companies: CompanyOption[];
  activeCompanyId: number | null;
  activeCompany: CompanyOption | null;
  setActiveCompanyId: (id: number) => void;
  selectCompany: (id: number) => Promise<void>;
  forgetCompany: (id: number) => Promise<void>;
  isLoading: boolean;
  isResolved: boolean;
};

const ActiveCompanyContext = createContext<ActiveCompanyContextValue | null>(null);

const readStoredCompanyId = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY);
  } catch {
    return null;
  }
};

const clearStoredCompanyId = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
  } catch {
    // Storage is unavailable in some private-browsing modes; the in-memory value still works.
  }
};

const writeStoredCompanyId = (id: number) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, String(id));
  } catch {
    // Storage is unavailable in some private-browsing modes; the in-memory value still works.
  }
};

type ActiveCompanyProviderProps = {
  children: ReactNode;
};

export const ActiveCompanyProvider = ({ children }: ActiveCompanyProviderProps) => {
  const [preferredCompanyId, setPreferredCompanyId] = useState<string | null>(() => readStoredCompanyId());

  const { data, isLoading, isFetched, refetch } = useQuery({
    queryKey: ['th', 'companies', 'options'],
    queryFn: () => getCompanyListOptions<{ value: string | number; label: string }>({}),
  });

  const companies = useMemo(() => normalizeCompanyOptions(data), [data]);

  const activeCompanyId = useMemo(() => {
    if (!isFetched) return parseStoredCompanyId(preferredCompanyId);
    return resolveActiveCompanyId(preferredCompanyId, companies);
  }, [isFetched, preferredCompanyId, companies]);

  useEffect(() => {
    if (!shouldPersistActiveCompanyId(preferredCompanyId, activeCompanyId)) return;
    writeStoredCompanyId(activeCompanyId as number);
  }, [preferredCompanyId, activeCompanyId]);

  const setActiveCompanyId = useCallback((id: number) => {
    setPreferredCompanyId(String(id));
  }, []);

  const selectCompany = useCallback(async (id: number) => {
    await refetch();
    setPreferredCompanyId(String(id));
  }, [refetch]);

  const forgetCompany = useCallback(async (id: number) => {
    setPreferredCompanyId((current) => (parseStoredCompanyId(current) === id ? null : current));
    if (parseStoredCompanyId(readStoredCompanyId()) === id) clearStoredCompanyId();
    await refetch();
  }, [refetch]);

  const activeCompany = useMemo(
    () => companies.find((company) => company.value === activeCompanyId) ?? null,
    [companies, activeCompanyId],
  );

  const value = useMemo<ActiveCompanyContextValue>(() => ({
    companies,
    activeCompanyId,
    activeCompany,
    setActiveCompanyId,
    selectCompany,
    forgetCompany,
    isLoading,
    isResolved: isFetched,
  }), [companies, activeCompanyId, activeCompany, setActiveCompanyId, selectCompany, forgetCompany, isLoading, isFetched]);

  return (
    <ActiveCompanyContext.Provider value={value}>
      {children}
    </ActiveCompanyContext.Provider>
  );
};

export const useActiveCompany = () => {
  const context = useContext(ActiveCompanyContext);
  if (!context) {
    throw new Error('useActiveCompany must be used within ActiveCompanyProvider');
  }
  return context;
};
